import { baseApi, API_TAGS, createCacheTags, Params, BaseQueryParams } from '@/shared/api';
import {
  MappingLocationsResponse,
  MappingDataResponse,
  MappingCorridorsResponse,
  MappingBreakdownResponse,
  createDashboardEndpointBuilder
} from '@/entities/dashboard';

import {
  CORRIDOR_TYPES,
  BREAKDOWN_TYPES,
  REQUEST_TYPES,
  type CorridorType,
  type BreakdownType,
  type RequestType
} from './constants';

const mappingEndpoint = createDashboardEndpointBuilder('/mapping', '/api/v2/dashboard');

const createMappingTags = (params?: Params) => [
  API_TAGS.DASHBOARD,
  API_TAGS.MAPPING,
  ...createCacheTags({ baseTag: API_TAGS.MAPPING, params })
];

interface WeekParams extends BaseQueryParams {
  weekNo?: number;
  organizationId?: string;
}

interface CorridorParams extends WeekParams {
  type?: CorridorType;
}

interface RequestParams extends WeekParams {
  type?: RequestType;
}

interface BreakdownParams extends BaseQueryParams {
  locationId: string;
  type?: BreakdownType;
  weekNo?: number;
}

export const mappingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMappingLocations: builder.query<MappingLocationsResponse, BaseQueryParams | void>({
      query: (params) => ({
        url: mappingEndpoint('/locations'),
        params: params || undefined
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'locations',
          projectId: arg?.projectId,
          siteId: arg?.siteId
        })
    }),

    getMappingStock: builder.query<MappingDataResponse, WeekParams | undefined>({
      query: (params) => ({
        url: mappingEndpoint('/stock'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'stock',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          organizationId: arg?.organizationId
        })
    }),

    getMappingCorridors: builder.query<MappingCorridorsResponse, CorridorParams | undefined>({
      query: (params = { type: CORRIDOR_TYPES.IN_TRANSIT }) => ({
        url: mappingEndpoint('/corridors'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'corridors',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          type: arg?.type
        })
    }),

    getMappingReleases: builder.query<MappingDataResponse, WeekParams | undefined>({
      query: (params) => ({
        url: mappingEndpoint('/releases'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'releases',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo
        })
    }),

    getMappingRequests: builder.query<MappingDataResponse, RequestParams | undefined>({
      query: (params = { type: REQUEST_TYPES.CONSIGNMENTS }) => ({
        url: mappingEndpoint('/requests'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'requests',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          type: arg?.type
        })
    }),

    getMappingBreakdown: builder.query<MappingBreakdownResponse, BreakdownParams>({
      query: ({ locationId, type = BREAKDOWN_TYPES.BY_ORGANIZATION, weekNo, projectId, siteId }) => ({
        url: mappingEndpoint('/breakdown'),
        params: { locationId, type, weekNo, projectId, siteId }
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'breakdown',
          projectId: arg.projectId,
          siteId: arg.siteId,
          weekNo: arg.weekNo,
          locationId: arg.locationId,
          type: arg.type || BREAKDOWN_TYPES.BY_ORGANIZATION
        })
    })
  })
});

export const {
  useGetMappingLocationsQuery,
  useGetMappingStockQuery,
  useGetMappingCorridorsQuery,
  useGetMappingReleasesQuery,
  useGetMappingRequestsQuery,
  useGetMappingBreakdownQuery
} = mappingApi;
